import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, ReplaySubject, Subject } from "rxjs";
import { DevicesData } from "../models/devices-data.model";
import { GeneralRequestService } from "./general-request.service";
import {
  ActivityTypes,
  Conditions,
  Feelings,
  FetchClassificatorsService,
  GoalTypes,
  ObservationTypes,
  UnitValues,
} from "./fetch-classificators.service";
import { ObservationData } from "../models/observation.model";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { UserInfoService } from "./user-info.service";
import { NgThrottle } from "../decorators/throttle.decorator";
import { ValueTransformService } from "./value-transform.service";
import deepmerge from "deepmerge";
import { GeneralRequestResponse } from "../models/payloads/shared/save-request.model";
import { getISOWeek, getYear } from "date-fns";


@Injectable({
  providedIn: "root",
})

export class NgxMeasurementsService implements OnDestroy {
  loadingData = new BehaviorSubject(false);
  offset = 0;

  scroller$ = new Subject<ScrollBar[]>();
  openAccordion = new BehaviorSubject(null);
  // observation data
  observationArray: ObservationData[] = [];
  observationsArrayChanges = new BehaviorSubject<ObservationData[]>([]);
  devicesArrayChanges = new ReplaySubject<DevicesData[]>();
  editingDeviceClassificator = new BehaviorSubject<number>(null);
  diarySpecificDefaultDevice: DevicesData = new DevicesData();
  deviceIsRequired = [];
  goalTypes: GoalTypes;
  // devices
  private devicesArray: DevicesData[] = [];
  // type codes
  private rawObservations = [];
  private subs = [];
  private obsTypes: ObservationTypes;
  private unitValues: UnitValues;
  private conditions: Conditions;
  private feelings: Feelings;
  private responseBody: GeneralRequestResponse;
  private previousClassificatorsUsed = [];
  private activityTypes: ActivityTypes;

  constructor(
    private valueTransformService: ValueTransformService,
    private generalRequestService: GeneralRequestService,
    private classificatorService: FetchClassificatorsService,
    private userService: UserInfoService) {
    const obsTypesSub = this.classificatorService.observationTypes.subscribe((data) => this.obsTypes = data);
    const activityTypesSub = this.classificatorService.activityTypes.subscribe((data) => this.activityTypes = data);
    const unitValuesSub = this.classificatorService.unitValues.subscribe((data) => this.unitValues = data);
    const conditionsSub = this.classificatorService.conditions.subscribe((data) => this.conditions = data);
    const feelingsSub = this.classificatorService.feelings.subscribe((data) => this.feelings = data);
    const goalTypeSub = this.classificatorService.goalTypes.subscribe((data) => this.goalTypes = data);
    this.subs.push(obsTypesSub, activityTypesSub, unitValuesSub, conditionsSub, feelingsSub, goalTypeSub);
  }

  get allObservations(): ObservationData[] {
    return this.observationArray;
  }

  get allDevices(): DevicesData[] {
    return this.devicesArray;
  }

  set allDevices(newDevices: DevicesData[]) {
    this.updateDevicesArray(newDevices);
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  // Recursively fetch all observations, allow to update all obs after 1 hour, but basically it's not necessary
  // at all because we keep observationArray[] up to date as we post/delete

  @NgThrottle(1000 * 60 * 60)
  fetchAllObservations(personId: number) {
    const classificatorsToFetch = [
      this.obsTypes.note,
      this.obsTypes.movement_VERY,
      this.obsTypes.movement_MODERATELY,
      this.obsTypes.movement_LIGHTLY,
      this.obsTypes.BMI,
      this.obsTypes.PEF,
      this.obsTypes.systolic,
      this.obsTypes.diastolic,
      this.obsTypes.pulse,
      this.obsTypes.bloodSugar,
      this.obsTypes.weight,
      this.obsTypes.waist,
      this.obsTypes.movementActivity,
      this.obsTypes.bloodPressure,
      this.obsTypes.height,
    ];
    this.previousClassificatorsUsed = classificatorsToFetch;
    return this.fetchObservations(classificatorsToFetch, personId);
  }


  fetchObservations(classificators: number[], personId) {
    let limit = 100;
    if (!this.devicesArray.length) this.fetchDevices(personId);

    this.generalRequestService.fetchObservationData(this.offset, personId, classificators).then((response: GeneralRequestResponse) => {
      this.responseBody = deepmerge(this.responseBody, response);
      // If response has more items than limit, then raise limit by offset and fetch again
      if (response.pagination.count > (limit + this.offset)) {
        this.offset += 100;
        this.fetchObservations(classificators, personId);
      } else {
        this.processResponse(this.responseBody);
        this.offset = 0;
        // console.log('All Observations finished fetching, resolving...');
        this.responseBody = null;
        this.generalRequestService.loadingStatus.isObservationsDataLoading = false;
        this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);
      }
    }).catch((err) => {
      // console.error(err);
    });
  }

  // Recursively fetch all observations
  fetchLatestObservations() {
    let personId = this.userService.getSessionContext().principal.id;
    // console.log('fetching observations for classificators [' + this.previousClassificatorsUsed + '] for personId: ' + personId);
    return this.generalRequestService.fetchObservationData(0, personId, this.previousClassificatorsUsed).then((response: GeneralRequestResponse) => {
      this.responseBody = deepmerge(this.responseBody, response);
      // console.log(this.responseBody);
      this.processResponse(response);
      // console.log('All Observations finished fetching, resolving...');
      this.generalRequestService.loadingStatus.isObservationsDataLoading = false;
      this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);
    });
  }

  postObservation(newObs: ObservationData) {
    this.generalRequestService.loadingStatus.isObservationsDataLoading = true;
    this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);
    if (!newObs.personId) {
      newObs.personId = this.userService.getSessionContext().principal.id;
    }
    this.generalRequestService.postNewObservation(newObs).then((response) => {
      this.processResponse(response);
      this.generalRequestService.loadingStatus.isObservationsDataLoading = false;
      this.generalRequestService.loadingDataStatus.next(this.generalRequestService.loadingStatus);
    });

  }

  deleteObservation(deleteObs: ObservationData) {
    this.generalRequestService.deleteObservation(deleteObs).then((response) => this.processResponse(response));
  }

  fetchDevices(personId: number) {
    this.generalRequestService.fetchDevices(personId).then((response) => this.processResponse(response));
  }

  async deleteDevice(deleteDevice: DevicesData): Promise<GeneralRequestResponse> {
    return this.generalRequestService.deleteDevice(deleteDevice).then((response) => {
      this.processResponse(response);
      return response;
    });
  }

  updateDevices(updateDevice: DevicesData[]) {
    this.generalRequestService.updateDevices(updateDevice).then((response) => {
      this.processResponse(response);
    });
  }

  // Process http response
  async processResponse(data: GeneralRequestResponse) {
    if (data) {
      if (data.delete) {
        await this.deleteData(data);
      } else if (data.items.some((item) => item.type == "device")) {
        this.allDevices = data.items;
      } else if (data.items.some((item) => item.type == "observation")) {
        this.rawObservations = deepmerge(this.rawObservations, data.items);
        await this.updateObservationsArray();
      }
    }
  }

  updateObservationsArray() {
    // Find children and masters from raw data
    let masters: ObservationData[] = [] = this.rawObservations.filter((newObs) => !newObs.masterObservationId);
    let children: ObservationData[] = this.rawObservations.filter((newObs) => newObs.masterObservationId);

    for (let master of masters) {
      // Give masters observationList if it doesn't have one
      if (!master.observationList) {
        Object.defineProperty(master, "observationList", { value: [], writable: true });
      }
      for (let child of children) {
        let foundMaster = masters.find((master) => master.id == child.masterObservationId);
        if (foundMaster && foundMaster.observationList) {
          if (!foundMaster.observationList.some((find) => find.id == child.id)) {
            foundMaster.observationList.push(child);
          }
        }
      }
      for (let child of master.observationList) {
        if (!child.conditionCodeId && master.classificatorCodeId) {
          child.conditionCodeId = master.conditionCodeId;
        }
      }
    }

    // Populate observationArray only with masters with unique id
    for (let master of masters) {
      if (!this.observationArray.some((old) => old.id == master.id)) {
        this.observationArray.push(master);
      }
    }
    // Notes are without masters
    let allNotes = children.filter((child) => child.classificatorCodeId == this.obsTypes.note);
    for (let note of allNotes) {
      this.observationArray.push(note);
    }

    this.assignDate();
    this.convertData();
    this.observationsArrayChanges.next(this.observationArray);
  }

  convertData() {
    for (let obs of this.observationArray) {
      // For blood sugar add mmol and mgdl fields to the observation
      if (obs.classificatorCodeId == this.obsTypes.bloodSugar) {
        for (let child of obs.observationList) {
          if (!child.mmol || !child.mgdl) {
            if (child.valueUnitId == this.unitValues.mmol_l) {
              let mmol = { mmol: child.value };
              Object.assign(child, mmol);
              let mgdl = { mgdl: this.valueTransformService.transform(obs, "mgdlOneDecimal").toString() };
              Object.assign(child, mgdl);
            } else if (child.valueUnitId == this.unitValues.mg_dl) {
              let mgdl = { mgdl: child.value };
              Object.assign(child, mgdl);
              let mmol = { mmol: this.valueTransformService.transform(obs, "toMmolValue").toString() };
              Object.assign(child, mmol);
            }
          }
        }
      }
    }
  }

  // Remove item and notify subscribers
  deleteData(data: GeneralRequestResponse) {
    // console.log('deleting...');
    // console.log(data.items);
    for (let item of data.items) {
      if (data.items.some((item) => item.type == "observation")) {
        this.observationArray = this.observationArray.filter(oldObs => oldObs.id != item.id);
        this.rawObservations = this.rawObservations.filter((oldObs) => oldObs.id != item.id);
        this.rawObservations = this.rawObservations.filter((oldObs) => oldObs.masterObservationId != item.id);
        this.observationsArrayChanges.next(this.observationArray);
      } else if (data.items.some((item) => item.type == "device")) {
        this.devicesArray = this.devicesArray.filter(oldDevice => oldDevice.id != item.id);
        this.devicesArrayChanges.next(this.devicesArray);
      }
    }
  }

  /**
   * Makes sure observation always has effectiveTime to be accessed
   * Preferred is actual data effectiveTime but when that is absent then insertTime is used
   */
  assignDate() {
    for (let obs of this.observationArray) {
      if (obs.observationList) {
        for (let child of obs.observationList) {
          if (child.effectiveTime) {
            return;
          } else if (!child.effectiveTime && child.insertTime) {
            Object.assign(child, { effectiveTime: child.insertTime });
          } else if (!child.effectiveTime && !child.insertTime) {
            Object.assign(child, { effectiveTime: new Date() });
          }
        }
      } else {
        if (obs.effectiveTime) {
          return;
        } else if (!obs.effectiveTime && obs.insertTime) {
          Object.assign(obs, { effectiveTime: obs.insertTime });
        } else if (!obs.effectiveTime && !obs.insertTime) {
          Object.assign(obs, { effectiveTime: new Date() });
        }
      }
    }
  }

  // [maxLength] can verify only text type, so numbers have to be checked using this method.
  checkIsNumber(event: KeyboardEvent, value: string) {
    let allowedChars = new Set();
    allowedChars.add("Backspace");
    allowedChars.add("Delete");
    allowedChars.add("ArrowLeft");
    allowedChars.add("ArrowRight");
    allowedChars.add("Period");
    if (allowedChars.has(event.code)) {
      return;
    } else if (value.length > 4) {
      event.preventDefault();
    }
  }

  // Give key if provided value
  getTypeKeyOrValue(unitValue: number) {
    for (const [key, value] of Object.entries(this.unitValues)) {
      if (value == unitValue) {
        return key;
      }
    }
  }

  // Return only observations without master or siblings
  getObservations(allObs: ObservationData[], obsType: number): ObservationData[] {
    let temp: ObservationData[] = [];
    for (let obs of allObs) {
      if (obs.observationList) {
        if (obs.observationList.some((child) => child.classificatorCodeId == obsType)) {
          temp.push(obs.observationList.find((child) => child.classificatorCodeId == obsType));
        }
      }
    }
    return temp;
  }

  // Return single latest observation
  getLatestObservation(allObs: ObservationData[], obsType: number) {
    let sameTypes = this.getObservations(allObs, obsType);
    return applySort(sameTypes, true).shift();
  }

  // Returns selected date from calendar picker
  onSelectedDate(event: MatDatepickerInputEvent<unknown>) {
    if (event.value) {
      return new Date(event.value.toString());
    }
  }

  // Returns selected time from time picker
  onSelectedTime(event: string) {
    let hours = Number(event.slice(0, 2));
    let minutes = Number(event.slice(3, 5));
    return new Date(1970, 0, 0, hours, minutes);
  }

  // Returns selected unit value type key
  getTypeKey(valueNumber: number) {
    for (let [key, value] of Object.entries(this.unitValues)) {
      if (value == valueNumber) {
        return key;
      }
    }
  }

  // Returns current time
  setCurrentTime() {
    const today = new Date();
    return today.getHours() + ":" + today.getMinutes();
  }

  // Returns unit value types in array that fit for an observations
  getUnitValuesFromObsCode(locationCode: number) {
    if (!locationCode) return;
    let allowedUnitValueCodes: number[];
    if (locationCode == this.obsTypes.PEF) allowedUnitValueCodes = [this.unitValues.l_min];
    if (locationCode == this.obsTypes.bloodSugar) allowedUnitValueCodes = [
      this.unitValues.mmol_l,
      this.unitValues.mg_dl,
    ];
    if (locationCode == this.obsTypes.bloodPressure) allowedUnitValueCodes = [this.unitValues.mmHg];

    let allowedUnitValues = {};
    for (let [key, value] of Object.entries(this.unitValues)) {
      if (allowedUnitValueCodes.includes(value)) {
        Object.assign(allowedUnitValues, { [key]: value });
      }
    }
    return allowedUnitValues;
  }

  // Returns filtered object
  getFilteredClassificators(original: object, allowed: number[]) {
    let allowedClassificatorsCodes = Object.values(original).filter((value) => allowed.includes(value));
    let allowedConditions = {};
    for (let code of allowedClassificatorsCodes) {
      for (let [key, value] of Object.entries(original)) {
        if (code == value) {
          Object.assign(allowedConditions, { [key]: value });
        }
      }
    }
    return allowedConditions;
  }

  // Return only observations without master or siblings
  getObservationsAsSingles(): ObservationData[] {
    let temp: ObservationData[] = [];
    this.allObservations.forEach((obs) => {
      if (obs.observationList) {
        obs.observationList.forEach((child) => temp.push(child));
      }
    });
    return temp;
  }

  /**
   * Return child observations without master
   * @param obs when given returns only from section
   */
  getObsAsSingles(obs?: ObservationData[]): ObservationData[] {
    if (obs) {
      let filtered = obs.filter(master => master.observationList).map(master => master.observationList);
      return [].concat.apply([], filtered);
    } else {
      let filtered = this.observationArray.filter(master => master.observationList).map(master => master.observationList);
      return [].concat.apply([], filtered);
    }
  }

  getClassificatorCodesByGoalType(goalTypeId: number): number[] {
    if (!this.goalTypes) return;
    if (goalTypeId == this.goalTypes.BLOOD_PRESSURE) {
      return [this.obsTypes.diastolic, this.obsTypes.systolic, this.obsTypes.pulse];
    } else if (goalTypeId == this.goalTypes.PEF) {
      return [this.obsTypes.PEF];
    } else if (goalTypeId == this.goalTypes.WEIGHT) {
      return [this.obsTypes.weight, this.obsTypes.waist, this.obsTypes.height];
    } else if (goalTypeId == this.goalTypes.BLOOD_SUGAR) {
      return [this.obsTypes.bloodSugar];
    }
  }

  // Return single latest observation
  getLatestObservationByDateAndType(date: Date, classificatorCode: number, personId: number) {
    let sameTypes = this.getObservations(this.observationArray, classificatorCode).filter(obs => obs.personId === personId);
    let weekObs = sameTypes.filter((obs) => {
      const obsDate = new Date(obs.effectiveTime);
      return getYear(obsDate) === getYear(date) && getISOWeek(obsDate) === getISOWeek(date);
    });
    return applySort(weekObs, true)[0];
  }

  private updateDevicesArray(newDevices: DevicesData[]) {
    let oldDevices = this.devicesArray;
    // if list was empty on init
    if (oldDevices.length == 0) {
      this.devicesArray = newDevices;
    } else {
      newDevices.forEach((newDevice) => {
        // If device was updated
        if (oldDevices.some((oldDevice) => oldDevice.id == newDevice.id)) {
          oldDevices.splice(oldDevices.indexOf(oldDevices.find((oldDevice) => oldDevice.id == newDevice.id)), 1);
          oldDevices.push(newDevice);
        }
        // If newDevice is set as default update other similar type not default
        if (newDevice.defaultDevice) {
          let sameTypes = oldDevices.filter((oldDevice) => oldDevice.observationTypeId == newDevice.observationTypeId);
          if (sameTypes.some((oldDevice) => oldDevice.defaultDevice && oldDevice.id != newDevice.id)) {
            sameTypes.find((oldDevice) => oldDevice.defaultDevice && oldDevice.id != newDevice.id).defaultDevice = false;
          }
        }
        oldDevices.push(newDevice);

      });
    }
    this.devicesArray = [...new Set(this.devicesArray)];
    this.devicesArrayChanges.next(this.devicesArray);
  }

}

export interface ScrollBar {
  elementId: string;
  value: string;
}

/**
 * Waits for an element satisfying selector to exist, then resolves promise with the element.
 * @param selector
 * @returns {Promise}
 * @reject executor, handle error (not used)
 */
export function elementReady(selector) {
  return new Promise((resolve) => {
    let el = document.querySelector(selector);
    if (el) {
      resolve(el);
    }
    new MutationObserver((mutationRecords, observer) => {
      // Query for elements matching the specified selector
      for (let element of Array.from(document.querySelectorAll(selector))) {
        resolve(element);
        // Once we have resolved we don't need the observer anymore.
        observer.disconnect();
      }
    })
      .observe(document.documentElement, {
        childList: true, subtree: true,
      });
  });
}

/**
 * @sortDec if true then show newest ↓ oldest
 */
export function applySort(observations: any[], sortDesc: boolean) {
  if (sortDesc) {
    return observations.sort((a, b) => (new Date(b.effectiveTime)).getTime() - (new Date(a.effectiveTime)).getTime());
  } else {
    return observations.sort((a, b) => (new Date(a.effectiveTime)).getTime() - (new Date(b.effectiveTime)).getTime());
  }
}

// [maxLength] can verify only text type, so numbers have to be checked using this method.
export function checkIsNumber(event: KeyboardEvent) {
  let allowedChars = new Set();
  allowedChars.add("Digit1").add("Numpad1");
  allowedChars.add("Digit2").add("Numpad2");
  allowedChars.add("Digit3").add("Numpad3");
  allowedChars.add("Digit4").add("Numpad4");
  allowedChars.add("Digit5").add("Numpad5");
  allowedChars.add("Digit6").add("Numpad6");
  allowedChars.add("Digit7").add("Numpad7");
  allowedChars.add("Digit8").add("Numpad8");
  allowedChars.add("Digit9").add("Numpad9");
  allowedChars.add("Digit0").add("Numpad0");
  allowedChars.add("Backspace");
  allowedChars.add("Delete");
  allowedChars.add("ArrowLeft");
  allowedChars.add("ArrowRight");
  allowedChars.add("Period");
  if (!allowedChars.has(event.code)) {
    event.preventDefault();
  }
}

/**
 * @value numeric float number
 * @precision to round to
 * round(12345.6789) // 12346
 * round(12345.6789, 2) // 12345.68
 * round(12345.6789, 1) // 12345.7
 * round(456.7, 2).toFixed(2) // "456.70"
 */
export function round(value, precision) {
  const multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}


